import { FC, useState, useEffect, useRef } from 'react';
import { css, Theme, useTheme } from '@emotion/react';
import { KeyReturn, EnvelopeSimple, Users, Trash, Info, PaperPlaneRight } from '@phosphor-icons/react';
import type { InviteToAccountResponse } from 'api/account/handlers/inviteToAccountHandler';
import type { InviteToAccountRequest } from 'common/account/InviteToAccountDocuments';
import { getPlaceholderUrl } from 'common/commonUtil';
import Api from 'client/lib/Api';
import { useError } from 'client/lib/Error';
import { useUser } from 'client/lib/User';
import { Banner } from 'client/components/Banner';
import { Button } from 'client/components/Button';
import { Catalog, CatalogItemProps } from 'client/components/Catalog';
import { Dialog } from 'client/components/Dialog/Dialog';
import { Divider } from 'client/components/Divider';
import { Input } from 'client/components/Input';
import { Tag } from 'client/components/Tag';
import { Typography } from 'client/components/Typography';
import { borderRadius, applyEffect } from 'client/Theme';

const catalogStyle = css`
  margin-bottom: 16px;

  svg {
    transform: translateY(2px);
    opacity: 0.8;
  }
`;

const inputStyle = css`
  margin-bottom: 16px;

  .MuiInputBase-root:not(.Mui-focused) {
    > label {
      display: none;
    }
  }
`;

const enterStyle = css`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 4px;
  opacity: 0.8;
  pointer-events: none;

  > svg {
    transform: translateY(-1px);
  }
`;

const usersStyle = (theme: Theme) => css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  margin-bottom: 16px;
  padding: 0;
  list-style: none;

  > li {
    display: flex;
    align-items: center;
    height: 42px;
    background-color: ${theme.color.grey[60]};
    border-radius: ${borderRadius}px;
    overflow: hidden;

    p {
      padding: 0 4px 0 12px;
    }

    button {
      padding: 0;
      width: 42px;
      height: 42px;
      border: none;
      background: none;
      color: ${theme.color.red[100]};
      cursor: pointer;

      &:hover {
        background: ${applyEffect(theme.color.grey[60], 'hover')};
      }
    }
  }
`;

export const inviteToAccount = (request: InviteToAccountRequest) =>
  Api.post<InviteToAccountRequest, InviteToAccountResponse>('/account/invite/create', request);

export interface InviteUsersDialogProps {
  open: boolean;
  accountID: number;
  onClose: () => void;
}

export const InviteUserDialog: FC<InviteUsersDialogProps> = ({ open, accountID, onClose }) => {
  const { user } = useUser();
  const theme = useTheme();
  const { setError } = useError({ showSnackbar: true });
  const { updateSession } = useUser();
  const [email, setEmail] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  const account = user?.accounts.find((account) => account.id === accountID);

  const accountItem: CatalogItemProps = {
    id: 'account',
    title: account?.name || '',
    subtitle: (
      <>
        <Info weight="bold" /> Users will be invited to join this account
      </>
    ),
    image: { alt: 'account profile picture', src: getPlaceholderUrl('account', accountID) },
  };

  useEffect(() => {
    if (!open) return;
    setEmail('');
    setEmails([]);

    setTimeout(() => {
      nameInputRef.current?.focus();
    }, 0);
  }, [open]);

  const handleEmailAdd = () => {
    const trimmedEmail = email.trim();
    if (emails.includes(trimmedEmail)) return;
    setEmails((state) => [...state, trimmedEmail]);
    setEmail('');
  };

  const handleEmailDelete = (email: string) => {
    setEmails((state) => state.filter((value) => value !== email));
  };

  const handleSend = async () => {
    setError(null);
    setIsLoading(true);

    const inviteEmails = [...emails];
    if (email.includes('@')) inviteEmails.push(email.trim());

    const response = await inviteToAccount({ accountID, emails: inviteEmails });
    if (response.status !== 'ok') {
      setIsLoading(false);
      return setError('Something went wrong while sending invites. Please try again…', { status: (response as any).status });
    }

    await updateSession();

    setIsLoading(false);

    onClose();
  };

  return (
    <Dialog
      open={open}
      title="Invite Users"
      description="Invite users to join an existing account via email"
      icon={<Users color={theme.color.primary[100]} />}
      iconOrnament={<EnvelopeSimple color={theme.color.primary[100]} />}
      actions={
        <>
          <Button primary icon={<PaperPlaneRight />} loading={isLoading} onClick={handleSend}>
            Send Invite{emails.length >= 2 ? 's' : ''}
          </Button>
        </>
      }
      onClose={onClose}
    >
      <Catalog items={[accountItem]} css={catalogStyle} />

      <Divider />

      <Input
        label="Email"
        value={email}
        icon={
          <Tag css={enterStyle}>
            <KeyReturn size={18} /> to add
          </Tag>
        }
        large
        onEnter={handleEmailAdd}
        onChange={(event) => setEmail(event.currentTarget.value)}
        ref={(input) => {
          nameInputRef.current = input;
        }}
        css={inputStyle}
      />

      {!!emails.length && (
        <ul css={usersStyle}>
          {emails.map((item) => (
            <li>
              <Typography>{item}</Typography>
              <button onClick={() => handleEmailDelete(item)}>
                <Trash size={16} weight="fill" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <Banner>Users will be added as members. You can change their access role after the invites have been sent.</Banner>
    </Dialog>
  );
};
